import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GroupProvider } from "@/context/GroupContext";
import { PlatformProvider } from "@/context/PlatformContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import CreateGroup from "@/pages/CreateGroup";
import Dashboard from "@/pages/Dashboard";
import Wallet from "@/pages/Wallet";
import Groups from "@/pages/Groups";
import GroupDetails from "@/pages/GroupDetails";
import AddExpense from "@/pages/AddExpense";
import ExpenseDetails from "@/pages/ExpenseDetails";
import EditExpense from "@/pages/EditExpense";
import Members from "@/pages/Members";
import MemberDetails from "@/pages/MemberDetails";
import SettlementHistory from "@/pages/SettlementHistory";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Subscription from "@/pages/Subscription";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import RefundPolicy from "@/pages/RefundPolicy";
import AIChatbot from "@/components/AIChatbot";
import ScrollToTop from "@/components/ScrollToTop";
import { AdminLayout, AdminOverview, AdminUsers, AdminGroups, AdminTransactions, AdminPlan, AdminBranding, AdminLeads } from "@/pages/Admin";

function AuthGate({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RootRoute() {
  const { user } = useAuth();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }
  return <Landing />;
}

function WorkspaceRedirect() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) return;
    if (!user || user === false) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.role === "superadmin") {
      navigate("/admin", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/groups");
        if (cancelled) return;
        if (!data.length) {
          navigate("/new", { replace: true });
          return;
        }
        const remembered = localStorage.getItem("activeGroupSlug");
        const target = data.find((g) => g.slug === remembered) || data[0];
        navigate(`/${target.slug}`, { replace: true });
      } catch (_) {
        if (!cancelled) navigate("/new", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-pulse text-slate-500 text-sm">Loading workspace…</div>
    </div>
  );
}

function AppRoutes() {
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <PlatformProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutesWithAdmin />
          <AIChatbot />
          <Toaster position="top-center" richColors closeButton />
        </BrowserRouter>
      </PlatformProvider>
    </AuthProvider>
  );
}

function AppRoutesWithAdmin() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/" element={<RootRoute />} />
      <Route path="/workspace" element={<WorkspaceRedirect />} />
      <Route path="/dashboard" element={<WorkspaceRedirect />} />
      <Route path="/new" element={<AuthGate><CreateGroup /></AuthGate>} />

      {/* Admin panel */}
      <Route path="/admin" element={<AuthGate><AdminLayout /></AuthGate>}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="groups" element={<AdminGroups />} />
        <Route path="transactions" element={<AdminTransactions />} />
        <Route path="plan" element={<AdminPlan />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="branding" element={<AdminBranding />} />
      </Route>

      <Route path="/switch" element={<AuthGate><GroupProvider><Layout /></GroupProvider></AuthGate>}>
        <Route index element={<Groups />} />
      </Route>
      <Route element={<AuthGate><GroupProvider><Layout /></GroupProvider></AuthGate>}>
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/subscription" element={<Subscription />} />
      </Route>
      <Route path="/:slug" element={<AuthGate><GroupProvider><Layout /></GroupProvider></AuthGate>}>
        <Route index element={<Dashboard />} />
        <Route path="add-expense" element={<AddExpense />} />
        <Route path="expenses/:expenseId" element={<ExpenseDetails />} />
        <Route path="expenses/:expenseId/edit" element={<EditExpense />} />
        <Route path="members" element={<Members />} />
        <Route path="members/:memberId" element={<MemberDetails />} />
        <Route path="settlements" element={<SettlementHistory />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="settings" element={<Settings />} />
        <Route path="group" element={<GroupDetails />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Old export left for compat — no longer used
function AppRoutesUnused() {
  return null;
}
